"use client";

import React, { useState, useEffect } from "react";
import { 
  Dumbbell, 
  Users, 
  Calendar, 
  TrendingUp, 
  RotateCcw, 
  Search, 
  Filter, 
  CheckCircle2,
  Sparkles,
  Activity
} from "lucide-react";
import GymKanban from "./GymKanban";
import GymStageModal from "./GymStageModal";
import GymDetailModal from "./GymDetailModal";
import { GymMemberLead } from "@/lib/demos/gymDb";

interface GymCrmDashboardProps {
  currency?: "INR" | "USD";
}

export default function GymCrmDashboard({ currency = "INR" }: GymCrmDashboardProps) {
  const [members, setMembers] = useState<GymMemberLead[]>([]);
  const [stats, setStats] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [tierFilter, setTierFilter] = useState("ALL");

  // Modals
  const [selectedMember, setSelectedMember] = useState<GymMemberLead | null>(null);
  const [isDetailOpen, setIsDetailOpen] = useState(false);

  const [pendingStageChange, setPendingStageChange] = useState<{
    member: GymMemberLead;
    targetStage: GymMemberLead["stage"];
  } | null>(null);

  const [toastMessage, setToastMessage] = useState<string | null>(null);

  const showToast = (msg: string) => {
    setToastMessage(msg);
    setTimeout(() => setToastMessage(null), 4000);
  };

  const fetchMembers = async () => {
    try {
      setLoading(true);
      const res = await fetch("/api/demos/gym/members");
      const data = await res.json();
      if (data.success) {
        setMembers(data.members);
        setStats(data.stats);
      }
    } catch (err) {
      console.error("Failed to load gym members:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMembers();
  }, []);

  const handleSelectMember = (m: GymMemberLead) => {
    setSelectedMember(m);
    setIsDetailOpen(true);
  };

  const handleUpdateStage = async (id: number, newStage: GymMemberLead["stage"]) => {
    const target = members.find((m) => m.id === id);
    if (!target || target.stage === newStage) return;

    // Modals for stages requiring user inputs
    if (
      newStage === "Workout & Tour Scheduled" ||
      newStage === "Assessment Completed" ||
      newStage === "Membership Proposed" ||
      newStage === "Active Member (Won)" ||
      newStage === "Cancelled / Inactive"
    ) {
      setPendingStageChange({
        member: target,
        targetStage: newStage,
      });
      return;
    }

    // Direct transition
    try {
      const res = await fetch(`/api/demos/gym/members/${id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ stage: newStage }),
      });
      const data = await res.json();
      if (data.success) {
        setMembers((prev) =>
          prev.map((m) => (m.id === id ? data.member : m))
        );
        showToast(data.automationMessage || `Member moved to ${newStage}`);
      }
    } catch (err) {
      console.error("Failed to update stage:", err);
    }
  };

  const handleConfirmStageModal = async (opts: any) => {
    if (!pendingStageChange) return;
    const { member } = pendingStageChange;

    try {
      const res = await fetch(`/api/demos/gym/members/${member.id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(opts),
      });
      const data = await res.json();
      if (data.success) {
        setMembers((prev) =>
          prev.map((m) => (m.id === member.id ? data.member : m))
        );
        showToast(data.automationMessage || `Member moved to ${opts.stage}`);
      }
    } catch (err) {
      console.error("Failed to complete stage transition:", err);
    } finally {
      setPendingStageChange(null);
    }
  };

  const handleDeleteMember = async (id: number) => {
    try {
      const res = await fetch(`/api/demos/gym/members/${id}`, {
        method: "DELETE",
      });
      if (res.ok) {
        setMembers((prev) => prev.filter((m) => m.id !== id));
        showToast("Member lead removed from pipeline");
      }
    } catch (err) {
      console.error("Failed to delete member:", err);
    }
  };

  const filteredMembers = members.filter((m) => {
    const matchesSearch =
      m.lead_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      m.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (m.phone && m.phone.includes(searchQuery)) ||
      m.fitness_goal.toLowerCase().includes(searchQuery.toLowerCase());

    const matchesTier = tierFilter === "ALL" || m.membership_tier.includes(tierFilter);

    return matchesSearch && matchesTier;
  });

  const sym = currency === "USD" ? "$" : "₹";

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6 max-w-[1600px] mx-auto min-h-screen">
      {/* Toast */}
      {toastMessage && (
        <div className="fixed bottom-6 right-6 z-50 bg-stone-900 border border-amber-500/40 text-white px-4 py-3 rounded-xl shadow-2xl flex items-center gap-3 animate-in fade-in slide-in-from-bottom-3 duration-200">
          <CheckCircle2 className="w-5 h-5 text-emerald-400 shrink-0" />
          <span className="text-xs font-semibold">{toastMessage}</span>
        </div>
      )}

      {/* Header Bar */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 pb-4 border-b border-stone-800">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <span className="px-2.5 py-0.5 rounded-full bg-amber-500/10 border border-amber-500/30 text-[11px] font-bold text-amber-400 uppercase tracking-wider">
              Athletic Club Automation Demo
            </span>
            <span className="text-xs text-stone-400 font-medium">• Iron &amp; Pulse</span>
          </div>
          <h1 className="text-2xl sm:text-3xl font-extrabold text-white tracking-tight">
            Club Floor &amp; Member CRM
          </h1>
          <p className="text-xs sm:text-sm text-stone-400">
            Automated VIP 3-day passes, coach allocation, InBody diagnostics, and annual agreements.
          </p>
        </div>

        <div className="flex items-center gap-3">
          <button
            onClick={fetchMembers}
            className="p-2.5 rounded-xl bg-stone-900 hover:bg-stone-800 text-stone-300 border border-stone-800 text-xs font-semibold transition-colors flex items-center gap-1.5"
            title="Refresh pipeline data"
          >
            <RotateCcw className={`w-3.5 h-3.5 ${loading ? "animate-spin text-amber-400" : ""}`} />
            <span className="hidden sm:inline">Refresh</span>
          </button>
        </div>
      </div>

      {/* KPI Stats Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="p-4 rounded-2xl bg-stone-900/70 border border-stone-800/90 space-y-2">
          <div className="flex items-center justify-between text-stone-400">
            <span className="text-xs font-semibold">Total Prospects</span>
            <Users className="w-4 h-4 text-blue-400" />
          </div>
          <div className="text-2xl font-black text-white">
            {stats?.totalLeads ?? members.length}
          </div>
          <span className="text-[11px] text-stone-400 block">
            Across 6 Pipeline Stages
          </span>
        </div>

        <div className="p-4 rounded-2xl bg-stone-900/70 border border-stone-800/90 space-y-2">
          <div className="flex items-center justify-between text-stone-400">
            <span className="text-xs font-semibold">Tours Scheduled</span>
            <Calendar className="w-4 h-4 text-amber-400" />
          </div>
          <div className="text-2xl font-black text-amber-400">
            {stats?.scheduledTours ?? 2} Athletes
          </div>
          <span className="text-[11px] text-amber-300 block">
            VIP 3-Day Passes Active
          </span>
        </div>

        <div className="p-4 rounded-2xl bg-stone-900/70 border border-stone-800/90 space-y-2">
          <div className="flex items-center justify-between text-stone-400">
            <span className="text-xs font-semibold">Active Members</span>
            <Dumbbell className="w-4 h-4 text-emerald-400" />
          </div>
          <div className="text-2xl font-black text-emerald-400">
            {stats?.activeMembers ?? 2} Enrolled
          </div>
          <span className="text-[11px] text-stone-400 block">
            Locker Utilization: 74%
          </span>
        </div>

        <div className="p-4 rounded-2xl bg-stone-900/70 border border-stone-800/90 space-y-2">
          <div className="flex items-center justify-between text-stone-400">
            <span className="text-xs font-semibold">Booked Annual Dues</span>
            <TrendingUp className="w-4 h-4 text-indigo-400" />
          </div>
          <div className="text-2xl font-black text-indigo-400">
            {sym}{(stats?.totalRevenue ?? 210000).toLocaleString()}
          </div>
          <span className="text-[11px] text-stone-400 block">
            Annual Membership Contracts
          </span>
        </div>
      </div>

      {/* Search & Tier Filter */}
      <div className="flex flex-col sm:flex-row items-center justify-between gap-3 bg-stone-900/40 p-3 rounded-2xl border border-stone-800/80">
        <div className="relative w-full sm:w-80">
          <Search className="w-4 h-4 text-stone-500 absolute left-3.5 top-3 pointer-events-none" />
          <input
            type="text"
            placeholder="Search by name, email, athletic goal..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full bg-stone-950 border border-stone-800 rounded-xl pl-9 pr-4 py-2 text-xs text-white placeholder:text-stone-500 focus:outline-none focus:border-amber-500 transition-colors"
          />
        </div>

        <div className="flex items-center gap-2 w-full sm:w-auto">
          <Filter className="w-3.5 h-3.5 text-stone-400 shrink-0 hidden sm:block" />
          <select
            value={tierFilter}
            onChange={(e) => setTierFilter(e.target.value)}
            className="w-full sm:w-auto bg-stone-950 border border-stone-800 rounded-xl px-3 py-2 text-xs text-stone-300 focus:outline-none focus:border-amber-500 transition-colors"
          >
            <option value="ALL">All Membership Tiers</option>
            <option value="Black">Apex Black Card</option>
            <option value="Performance">Executive Performance</option>
            <option value="Conditioning">Strength &amp; Conditioning</option>
          </select>
        </div>
      </div>

      {/* Kanban Board */}
      <GymKanban
        members={filteredMembers}
        onSelectMember={handleSelectMember}
        onUpdateStage={handleUpdateStage}
        currency={currency}
      />

      {/* Stage Transition Modal */}
      {pendingStageChange && (
        <GymStageModal
          memberLead={pendingStageChange.member}
          targetStage={pendingStageChange.targetStage}
          isOpen={true}
          onClose={() => setPendingStageChange(null)}
          onConfirm={handleConfirmStageModal}
        />
      )}

      {/* Detail Modal */}
      <GymDetailModal
        member={selectedMember}
        isOpen={isDetailOpen}
        onClose={() => {
          setIsDetailOpen(false);
          setSelectedMember(null);
        }}
        onDelete={handleDeleteMember}
      />
    </div>
  );
}
